import z from 'zod'

import { zodErrorParser } from '@/utils/zod'
import { AssetsResponseSchema, BaseEntitySchema } from './Common'
import { SubjectResponseSchema, SubjectUserResponseSchema } from './Subject'

export enum AlertActivityChange {
  STATUS = 'STATUS',
  ASSIGNMENT = 'ASSIGNMENT',
  ESCALATED = 'ESCALATED',
  TYPE = 'TYPE',
  SELF_EVALUATION = 'SELF_EVALUATION',
}

export const AlertStatuses = [
  'NEW',
  'MONITOR',
  'ESCALATED',
  'ISSUE_TAKEDOWN',
  'RESOLVED',
] as const

export type AlertStatusType = typeof AlertStatuses[number]

const AlertOrigins = [
  'WATCHDOG',
  'PUBLIC_REPORT',
] as const

export type AlertOriginType = typeof AlertOrigins[number]

export const AlertTypes = [
  'COPYRIGHT',
  'FINANCIAL',
  'GENERAL',
  'IMPERSONATION',
  'INVALID',
  'LICENSING',
  'MALICIOUS',
  'MISINFORMATION',
  'POSITIVE',
  'SEXUAL',
  'VIOLENCE',
] as const

export type AlertTypesType = typeof AlertTypes[number]

export const AlertSources = [
  'GOOGLE',
  'GOOGLE_IMAGES',
  'YOUTUBE',
  'GO_FUND_ME'
] as const

export type AlertSourcesType = typeof AlertSources[number]

export const AlertAssetTypes = [
  'TEXT',
  'IMAGE',
  'THUMBNAIL',
] as const

const AlertDetailsResponseSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  title: z.string(),
  description: z.string().nullish(),
  usageType: z.array(z.enum(AlertTypes)).nullish(),
  source: z.enum(AlertOrigins),
  externalSource: z.enum(AlertSources).nullish(),
  sourceHref: z.string().nullish(),
  sourceSeo: z.string().nullish(),
  notMe: z.boolean().nullish(),
  authorized: z.boolean().nullish(),
  threat: z.boolean().nullish(),
}).strict()

export type AlertDetailsResponseType = z.infer<typeof AlertDetailsResponseSchema>

const AlertSubjectUserSchema = SubjectUserResponseSchema.extend({
  details: z.union([
    z.uuid(),
    z.object({
      id: z.uuid(),
    }).strict()
  ]),
}).strict()

const AlertSubjectSchema = SubjectResponseSchema.extend({
  details: z.union([
    z.uuid(),
    z.object({
      id: z.uuid(),
    }).strict()
  ]),
  group: z.union([
    z.uuid(),
    z.object({
      id: z.uuid(),
    }).strict()
  ]).nullish(),
  user: AlertSubjectUserSchema
}).strict()

const AlertActivityChangeSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  alertDetails: AlertDetailsResponseSchema.nullish(),
  assignedTo: z.union([
    z.uuid(),
    AlertSubjectUserSchema
  ]).nullish(),
  escalateTo: z.union([
    z.uuid(),
    AlertSubjectUserSchema
  ]).nullish(),
  rawS3ref: z.string().nullish(),
  status: z.enum(AlertStatuses)
}).strict()

const AlertActivitySchema = BaseEntitySchema.extend({
  id: z.uuid(),
  alert: z.union([
    z.uuid(),
    AlertActivityChangeSchema.extend({
      alertDetails: z.uuid(),
      subject: z.uuid()
    }).strict()
  ]),
  changeType: z.enum(AlertActivityChange),
  from: AlertActivityChangeSchema,
  to: AlertActivityChangeSchema,
  user: z.union([
    z.uuid(),
    AlertSubjectUserSchema.extend({
      assets: AssetsResponseSchema.nullish()
    }).strict()
  ]).nullish(),
}).strict()

export type AlertActivityType = z.infer<typeof AlertActivitySchema>

const AlertAssetsSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  alert: z.union([
    z.uuid(),
    z.object({
      id: z.uuid(),
    }).strict()
  ]).nullish(),
  url: z.string().nullish(),
  externalUrl: z.string().nullish(),
  type: z.string().nullish(),
}).strict()

const AlertResponseSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  alertDetails: AlertDetailsResponseSchema,
  status: z.enum(AlertStatuses),
  subject: AlertSubjectSchema,
  assignedTo: z.uuid().nullish(),
  escalateTo: z.uuid().nullish(),
  activity: z.array(AlertActivitySchema).nullish(),
  assets: z.array(AlertAssetsSchema).nullish(),
}).strict()

export type AlertResponse = z.infer<typeof AlertResponseSchema>

export const zodAlertResponseValidator = (
  data: AlertResponse[]
) => zodErrorParser(
  'AlertResponse',
  AlertResponseSchema,
  data,
)

const AlertCountsResponseSchema = BaseEntitySchema.extend({
  total: z.number(),
  counts: z.object(
    Object.fromEntries(
      AlertStatuses.map(status => [status, z.number()])
    )
  )
}).strict()

export type AlertCountsResponse = z.infer<typeof AlertCountsResponseSchema>

export const zodAlertCountsResponseValidator = (
  data: AlertCountsResponse
) => zodErrorParser(
  'AlertCountsResponse',
  AlertCountsResponseSchema,
  data,
)
